import React, { useState, useEffect, useMemo, useRef } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../../supabaseClient';
import { Task, PhysicalUnit, Level, ProjectInfo, ProtocolState, WeeklyPlan, GraphicPlan, GraphicPlanRegion, Responsible } from '../../types';
import { PdfKonvaViewer, PdfRegion, PdfRegionStripe, PdfRegionMarker, PdfKonvaViewerHandle } from './PdfKonvaViewer';

export interface GraphicProgressPlanningViewProps {
    activeProjectId: string;
    tasks: Task[];
    units: PhysicalUnit[];
    levels: Level[];
    matrixData: Record<string, any>;
    protocolConfigs: any;
    projectInfo: ProjectInfo;
    savedProtocols: Record<string, ProtocolState>;
    weeklyPlans: WeeklyPlan[];
    responsibles?: Responsible[];
    activePlanId?: number | null;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    onClose?: () => void;
}

// 8 variedades de colores bien diferenciados
const EIGHT_COLORS = [
    '#2563EB', // 1. Azul
    '#16A34A', // 2. Verde
    '#EA580C', // 3. Naranja
    '#9333EA', // 4. Morado / Violeta
    '#E11D48', // 5. Rojo / Carmesí
    '#0891B2', // 6. Cian / Turquesa
    '#CA8A04', // 7. Ámbar / Dorado
    '#1E3A8A', // 8. Azul Marino Oscuro
];

// Paleta extendida para tareas de superficie (rellenos sólidos)
const SURFACE_COLOR_PALETTE = [
    '#3B82F6', // Azul vibrante
    '#10B981', // Esmeralda
    '#F59E0B', // Ámbar
    '#8B5CF6', // Violeta
    '#EC4899', // Rosa
    '#06B6D4', // Cian
    '#84CC16', // Lima
    '#F97316', // Naranja
    '#6366F1', // Índigo
    '#14B8A6', // Teal
    '#D946EF', // Fucsia
    '#E11D48', // Carmesí
    '#0284C7', // Celeste oscuro
    '#65A30D', // Verde oliva
    '#D97706', // Ocre
    '#7C3AED', // Púrpura
];

// 5 figuras geométricas en el orden solicitado:
// Cuadrado, Rombo, Triángulo, Círculo, Estrella (x 8 colores = 40 combinaciones únicas)
const FIVE_SHAPES: ('square' | 'diamond' | 'triangle' | 'circle' | 'star')[] = [
    'square',
    'diamond',
    'triangle',
    'circle',
    'star'
];

// Generamos las 40 combinaciones únicas intercalando figura y color para máxima distinción visual inmediata,
// asegurando que existan exactamente:
// - Cuadrado: 8 variedades de colores
// - Rombo: 8 variedades de colores
// - Triángulos: 8 variedades de colores
// - Círculos: 8 variedades de colores
// - Estrella: 8 variedades de colores
const POINT_STYLE_COMBINATIONS: { shape: 'square' | 'diamond' | 'triangle' | 'circle' | 'star'; color: string }[] = (() => {
    const combos: { shape: 'square' | 'diamond' | 'triangle' | 'circle' | 'star'; color: string }[] = [];
    for (let cIdx = 0; cIdx < EIGHT_COLORS.length; cIdx++) {
        for (let sIdx = 0; sIdx < FIVE_SHAPES.length; sIdx++) {
            const color = EIGHT_COLORS[(cIdx + sIdx) % EIGHT_COLORS.length];
            const shape = FIVE_SHAPES[sIdx];
            combos.push({ shape, color });
        }
    }
    return combos;
})();

const SHAPE_LABELS: Record<'square' | 'diamond' | 'triangle' | 'circle' | 'star', string> = {
    square: 'Cuadrado',
    diamond: 'Rombo',
    triangle: 'Triángulo',
    circle: 'Círculo',
    star: 'Estrella'
};

/**
 * Renderiza el ícono SVG nítido para la leyenda lateral según la figura y color asignados
 */
const renderShapeBadgeSvg = (shape: 'square' | 'diamond' | 'triangle' | 'circle' | 'star', color: string) => {
    switch (shape) {
        case 'square':
            return (
                <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0">
                    <rect x="2.5" y="2.5" width="11" height="11" rx="1.5" fill={color} stroke="#ffffff" strokeWidth="1.2" />
                </svg>
            );
        case 'diamond':
            return (
                <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0">
                    <polygon points="8,1.5 14.5,8 8,14.5 1.5,8" fill={color} stroke="#ffffff" strokeWidth="1.2" />
                </svg>
            );
        case 'triangle':
            return (
                <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0">
                    <polygon points="8,2 14.5,13.5 1.5,13.5" fill={color} stroke="#ffffff" strokeWidth="1.2" />
                </svg>
            );
        case 'circle':
            return (
                <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0">
                    <circle cx="8" cy="8" r="5.8" fill={color} stroke="#ffffff" strokeWidth="1.2" />
                </svg>
            );
        case 'star':
            return (
                <svg width="14" height="14" viewBox="0 0 16 16" className="shrink-0">
                    <polygon
                        points="8,1.2 9.9,5.6 14.7,6.1 11.1,9.3 12.1,14.0 8,11.5 3.9,14.0 4.9,9.3 1.3,6.1 6.1,5.6"
                        fill={color}
                        stroke="#ffffff"
                        strokeWidth="1"
                    />
                </svg>
            );
    }
};

/**
 * Convierte nombres de nivel estándar en prefijos compactos y profesionales
 */
export const formatLevelPrefix = (rawName?: string): string => {
    if (!rawName) return '';
    const trimmed = rawName.trim();

    if (/^manzana\s*/i.test(trimmed)) {
        return trimmed.replace(/^manzana\s*/i, 'Mz ');
    }
    if (/^piso\s*/i.test(trimmed)) {
        return trimmed.replace(/^piso\s*/i, 'P');
    }
    if (/^subterr[aá]neo\s*/i.test(trimmed)) {
        return trimmed.replace(/^subterr[aá]neo\s*/i, 'Sub ');
    }
    if (/^edificio\s*/i.test(trimmed)) {
        return trimmed.replace(/^edificio\s*/i, 'Ed ');
    }
    if (/^zona\s*/i.test(trimmed)) {
        return trimmed.replace(/^zona\s*/i, 'Z');
    }
    if (/^sector\s*/i.test(trimmed)) {
        return trimmed.replace(/^sector\s*/i, 'Sec ');
    }
    if (/^torre\s*/i.test(trimmed)) {
        return trimmed.replace(/^torre\s*/i, 'T');
    }
    if (/^etapa\s*/i.test(trimmed)) {
        return trimmed.replace(/^etapa\s*/i, 'Et ');
    }

    return trimmed;
};

/**
 * Genera la etiqueta visible para un recinto combinando el prefijo de nivel y el nombre de la unidad física
 */
export const formatPhysicalUnitLabel = (unitName?: string, levelName?: string, fallbackLabel?: string): string => {
    if (!unitName) return fallbackLabel || '';
    const lvlPrefix = formatLevelPrefix(levelName);
    if (!lvlPrefix) return unitName;
    return `${lvlPrefix} - ${unitName}`;
};

export const GraphicProgressPlanningView: React.FC<GraphicProgressPlanningViewProps> = ({
    activeProjectId,
    tasks,
    units,
    levels,
    matrixData,
    protocolConfigs,
    projectInfo,
    savedProtocols,
    weeklyPlans,
    responsibles = [],
    activePlanId,
    isFullscreen: isFullscreenProp,
    onToggleFullscreen,
    onClose
}) => {
    const [plans, setPlans] = useState<GraphicPlan[]>([]);
    const [regions, setRegions] = useState<GraphicPlanRegion[]>([]);
    const [loading, setLoading] = useState(true);

    const [selectedPlanId, setSelectedPlanId] = useState<string>('');
    const [selectedPlanIdsForExport, setSelectedPlanIdsForExport] = useState<string[]>([]);
    const [isPlanDropdownOpen, setIsPlanDropdownOpen] = useState<boolean>(false);
    const planDropdownRef = useRef<HTMLDivElement>(null);

    // Estado para Pantalla Completa y Exportación
    const [internalFullscreen, setInternalFullscreen] = useState(false);
    const isFullscreen = isFullscreenProp !== undefined ? isFullscreenProp : internalFullscreen;
    const handleToggleFullscreen = () => {
        if (onToggleFullscreen) {
            onToggleFullscreen();
        } else {
            setInternalFullscreen(prev => !prev);
        }
    };
    const [isExportingPdf, setIsExportingPdf] = useState(false);
    const konvaViewerRef = useRef<PdfKonvaViewerHandle>(null);

    // Estado para el Popover interactivo
    const [popoverInfo, setPopoverInfo] = useState<{
        unit: PhysicalUnit;
        level?: Level;
        x: number;
        y: number;
        tasks: {
            task: Task;
            subcontractorName: string;
            percentComprometido?: number;
            percentCumplimiento?: number;
            executed?: boolean | null;
            statusText: string;
            statusColor: string;
        }[];
    } | null>(null);

    const containerRef = useRef<HTMLDivElement>(null);

    // Cerrar dropdown de selección de planos al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (planDropdownRef.current && !planDropdownRef.current.contains(e.target as Node)) {
                setIsPlanDropdownOpen(false);
            }
        };
        if (isPlanDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isPlanDropdownOpen]);

    // Fallbacks defensivos
    const safeUnits = units || [];
    const safeLevels = levels || [];
    const safeTasks = tasks || [];

    const loadData = async () => {
        setLoading(true);
        try {
            const [plansRes, regionsRes] = await Promise.all([
                supabase.from('graphic_plans').select('*').eq('project_id', activeProjectId),
                supabase.from('graphic_plan_regions').select('*').eq('project_id', activeProjectId)
            ]);

            if (plansRes.data) {
                setPlans(plansRes.data);
                if (plansRes.data.length > 0 && !selectedPlanId) {
                    const firstId = plansRes.data[0].id;
                    setSelectedPlanId(firstId);
                    setSelectedPlanIdsForExport([firstId]);
                }
            } else {
                setPlans([]);
            }

            if (regionsRes.data) {
                setRegions(regionsRes.data);
            } else {
                setRegions([]);
            }
        } catch (err) {
            console.error("Error cargando planos para avance gráfico de planificación", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeProjectId) loadData();
    }, [activeProjectId]);

    // Semana activa
    const currentActivePlan = useMemo(() => {
        if (activePlanId !== undefined && activePlanId !== null) {
            return weeklyPlans.find(p => p.id === activePlanId);
        }
        return weeklyPlans.find(p => p.status !== 'Cerrada') || weeklyPlans[weeklyPlans.length - 1];
    }, [weeklyPlans, activePlanId]);

    // Aplanar tareas
    const flatTasks = useMemo(() => {
        const list: Task[] = [];
        const traverse = (arr: Task[]) => {
            arr.forEach(t => {
                if (t.type === 'task') list.push(t);
                if (t.children && t.children.length > 0) traverse(t.children);
            });
        };
        traverse(safeTasks);
        return list;
    }, [safeTasks]);

    // Catálogo global de estilos únicos por partida programada en la semana
    // Garantiza que cada tarea puntual tenga una combinación única de (Figura + Color) entre las 40 disponibles
    const globalTaskStylesMap = useMemo(() => {
        const map = new Map<string, {
            task: Task;
            color: string;
            isSurface: boolean;
            shape: 'square' | 'diamond' | 'triangle' | 'circle' | 'star';
        }>();

        if (!currentActivePlan || !currentActivePlan.allocations) return map;

        let surfaceIdx = 0;
        let pointComboIdx = 0;

        currentActivePlan.allocations.forEach(alloc => {
            const foundTask = flatTasks.find(t => t.id === alloc.taskId);
            if (foundTask && !map.has(foundTask.id)) {
                const unitNorm = (foundTask.unit || '').toLowerCase().trim();
                const isSurface = unitNorm.includes('m2') || unitNorm.includes('m²') || unitNorm.includes('sup');

                if (isSurface) {
                    const color = SURFACE_COLOR_PALETTE[surfaceIdx % SURFACE_COLOR_PALETTE.length];
                    surfaceIdx++;
                    map.set(foundTask.id, {
                        task: foundTask,
                        color,
                        isSurface: true,
                        shape: 'square'
                    });
                } else {
                    const combo = POINT_STYLE_COMBINATIONS[pointComboIdx % POINT_STYLE_COMBINATIONS.length];
                    pointComboIdx++;
                    map.set(foundTask.id, {
                        task: foundTask,
                        color: combo.color,
                        isSurface: false,
                        shape: combo.shape
                    });
                }
            }
        });

        return map;
    }, [currentActivePlan, flatTasks]);

    // Helper para obtener las partidas programadas que aplican exclusivamente a un plano específico
    const getProgrammedTasksForPlan = React.useCallback((planId: string) => {
        if (!currentActivePlan || !currentActivePlan.allocations || !planId) return [];

        const planRegions = regions.filter(r => r.plan_id === planId && r.physical_unit_id);
        const planUnitIds = new Set(planRegions.map(r => r.physical_unit_id as string));

        const taskMap = new Map<string, {
            task: Task;
            color: string;
            isSurface: boolean;
            shape: 'square' | 'diamond' | 'triangle' | 'circle' | 'star';
            unitIds: Set<string>;
        }>();

        currentActivePlan.allocations.forEach(alloc => {
            if (planUnitIds.has(alloc.unitId)) {
                const styleInfo = globalTaskStylesMap.get(alloc.taskId);
                if (styleInfo) {
                    if (!taskMap.has(styleInfo.task.id)) {
                        taskMap.set(styleInfo.task.id, {
                            ...styleInfo,
                            unitIds: new Set([alloc.unitId])
                        });
                    } else {
                        taskMap.get(styleInfo.task.id)!.unitIds.add(alloc.unitId);
                    }
                }
            }
        });

        return Array.from(taskMap.values());
    }, [currentActivePlan, regions, globalTaskStylesMap]);

    // Partidas programadas exclusivamente para el plano que se está visualizando actualmente
    const weeklyProgrammedTasks = useMemo(() => {
        return getProgrammedTasksForPlan(selectedPlanId);
    }, [getProgrammedTasksForPlan, selectedPlanId]);

    // Plano activo
    const activePlan = useMemo(() => plans.find(p => p.id === selectedPlanId), [plans, selectedPlanId]);
    
    // Regiones del plano activo
    const activePlanRegions = useMemo(() => {
        return regions.filter(r => r.plan_id === selectedPlanId);
    }, [regions, selectedPlanId]);

    // Construir mapa de asignaciones por unidad para la semana
    const unitAllocationsMap = useMemo(() => {
        const map = new Map<string, typeof currentActivePlan.allocations>();
        if (!currentActivePlan || !currentActivePlan.allocations) return map;

        currentActivePlan.allocations.forEach(alloc => {
            if (!map.has(alloc.unitId)) {
                map.set(alloc.unitId, []);
            }
            map.get(alloc.unitId)!.push(alloc);
        });
        return map;
    }, [currentActivePlan]);

    // Mapeo a PdfRegion para Konva con reglas de Superficie vs Puntual
    const konvaRegions: PdfRegion[] = useMemo(() => {
        return activePlanRegions.map(reg => {
            const unit = safeUnits.find(u => u?.id === reg.physical_unit_id);
            const level = safeLevels.find(l => l?.id === reg.level_id);
            const shortLabel = formatPhysicalUnitLabel(unit?.name, level?.name, reg.label || '');

            if (!reg.physical_unit_id) {
                return {
                    id: reg.id,
                    pageNumber: reg.page_number || 1,
                    xPercent: reg.x_percent || 0,
                    yPercent: reg.y_percent || 0,
                    widthPercent: reg.width_percent || 10,
                    heightPercent: reg.height_percent || 10,
                    label: shortLabel,
                    color: 'rgba(203, 213, 225, 0.4)',
                    physicalUnitId: undefined
                };
            }

            const unitAllocs = unitAllocationsMap.get(reg.physical_unit_id) || [];
            
            // Separar tareas de superficie de las puntuales
            const surfaceStripes: PdfRegionStripe[] = [];
            const pointMarkers: PdfRegionMarker[] = [];

            unitAllocs.forEach(alloc => {
                const progTaskInfo = globalTaskStylesMap.get(alloc.taskId);
                if (progTaskInfo) {
                    if (progTaskInfo.isSurface) {
                        surfaceStripes.push({
                            color: progTaskInfo.color,
                            label: progTaskInfo.task.name,
                            weight: 1,
                            taskId: progTaskInfo.task.id
                        });
                    } else {
                        pointMarkers.push({
                            id: `${reg.id}-${progTaskInfo.task.id}`,
                            shape: progTaskInfo.shape,
                            color: progTaskInfo.color,
                            label: progTaskInfo.task.name,
                            taskId: progTaskInfo.task.id
                        });
                    }
                }
            });

            // Si no tiene tareas de superficie programadas pero sí tiene puntuales o ninguna
            const defaultBgColor = surfaceStripes.length === 0 
                ? (unitAllocs.length > 0 ? 'rgba(241, 245, 249, 0.6)' : 'rgba(226, 232, 240, 0.3)')
                : undefined;

            return {
                id: reg.id,
                pageNumber: reg.page_number || 1,
                xPercent: reg.x_percent || 0,
                yPercent: reg.y_percent || 0,
                widthPercent: reg.width_percent || 10,
                heightPercent: reg.height_percent || 10,
                label: shortLabel,
                levelName: formatLevelPrefix(level?.name),
                unitName: unit?.name,
                color: defaultBgColor,
                physicalUnitId: reg.physical_unit_id,
                stripes: surfaceStripes.length > 0 ? surfaceStripes : undefined,
                markers: pointMarkers.length > 0 ? pointMarkers : undefined
            };
        });
    }, [activePlanRegions, safeUnits, safeLevels, unitAllocationsMap, globalTaskStylesMap]);

    // Manejador de clic e interacción para Popover
    const handleRegionSelect = (reg: PdfRegion | null, mouseEvent?: any) => {
        if (!reg || !reg.physicalUnitId) {
            setPopoverInfo(null);
            return;
        }

        const unit = safeUnits.find(u => u.id === reg.physicalUnitId);
        if (!unit) return;

        const level = safeLevels.find(l => l.id === unit.levelId);
        const unitAllocs = unitAllocationsMap.get(unit.id) || [];

        const tasksInfo = unitAllocs.map(alloc => {
            const t = flatTasks.find(item => item.id === alloc.taskId);
            const resp = responsibles.find(r => r.id === alloc.responsibleId);
            
            let statusText = 'Planificada';
            let statusColor = 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300';

            if (alloc.executed === true || (alloc.percentCumplimiento !== undefined && alloc.percentCumplimiento >= 100)) {
                statusText = 'Ejecutado (100%)';
                statusColor = 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
            } else if (alloc.executed === false) {
                statusText = 'No Ejecutado';
                statusColor = 'text-red-600 bg-red-50 border-red-200 dark:bg-red-950/40 dark:text-red-300';
            } else if (alloc.percentCumplimiento !== undefined && alloc.percentCumplimiento > 0) {
                statusText = `Avance: ${alloc.percentCumplimiento}%`;
                statusColor = 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300';
            }

            return {
                task: t || { id: alloc.taskId, name: alloc.additionalTaskName || 'Partida Programada', type: 'task' as const },
                subcontractorName: resp ? resp.name : 'Sin Subcontrato asignado',
                percentComprometido: alloc.percentComprometido,
                percentCumplimiento: alloc.percentCumplimiento,
                executed: alloc.executed,
                statusText,
                statusColor
            };
        });

        let popoverX = 300;
        let popoverY = 150;
        if (mouseEvent && mouseEvent.clientX) {
            popoverX = Math.min(window.innerWidth - 380, mouseEvent.clientX + 15);
            popoverY = Math.min(window.innerHeight - 300, mouseEvent.clientY + 15);
        }

        setPopoverInfo({
            unit,
            level,
            x: popoverX,
            y: popoverY,
            tasks: tasksInfo
        });
    };

    // Exportación a PDF de 1 o múltiples planos seleccionados en un mismo documento PDF
    const handleExportPdf = async () => {
        if (!konvaViewerRef.current) return;
        setIsExportingPdf(true);

        const originalPlanId = selectedPlanId;
        const plansToExportIds = selectedPlanIdsForExport.length > 0
            ? plans.filter(p => selectedPlanIdsForExport.includes(p.id)).map(p => p.id)
            : (selectedPlanId ? [selectedPlanId] : []);

        if (plansToExportIds.length === 0) {
            setIsExportingPdf(false);
            return;
        }

        const yieldThread = (ms = 40) => new Promise<void>(resolve => {
            requestAnimationFrame(() => setTimeout(resolve, ms));
        });

        // Espera a que el visor Konva cargue y renderice un plano cuando se cambia dinámicamente
        const waitForPlanRender = async (targetPlanId: string) => {
            if (selectedPlanId !== targetPlanId) {
                setSelectedPlanId(targetPlanId);
                // Dar tiempo a React y pdfjs para renderizar el canvas del nuevo plano
                for (let attempt = 0; attempt < 25; attempt++) {
                    await yieldThread(120);
                    const dims = konvaViewerRef.current?.getPdfDimensions();
                    if (dims && dims.width > 0 && dims.height > 0) {
                        await yieldThread(180);
                        break;
                    }
                }
            } else {
                await yieldThread(50);
            }
        };

        try {
            let doc: jsPDF | null = null;

            for (let i = 0; i < plansToExportIds.length; i++) {
                const targetPlanId = plansToExportIds[i];
                const targetPlan = plans.find(p => p.id === targetPlanId);
                if (!targetPlan) continue;

                await waitForPlanRender(targetPlanId);

                if (!konvaViewerRef.current) continue;
                const dataUrl = await konvaViewerRef.current.toDataURLAsync();
                if (!dataUrl) continue;

                await yieldThread();

                const dims = konvaViewerRef.current.getPdfDimensions();
                const isLandscape = dims.width >= dims.height;
                const orientation = isLandscape ? 'landscape' : 'portrait';

                if (!doc) {
                    doc = new jsPDF({
                        orientation,
                        unit: 'mm',
                        format: 'a3'
                    });
                } else {
                    doc.addPage('a3', orientation);
                }

                const pageWidth = doc.internal.pageSize.getWidth();
                const pageHeight = doc.internal.pageSize.getHeight();

                // Membrete Superior
                doc.setFillColor(30, 41, 59); // slate-800
                doc.rect(0, 0, pageWidth, 22, 'F');

                doc.setTextColor(255, 255, 255);
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text(`AVANCE GRÁFICO DE PLANIFICACIÓN — ${projectInfo.name.toUpperCase()}`, 12, 11);

                doc.setFontSize(9);
                doc.setFont('helvetica', 'normal');
                const planLabel = targetPlan.nombre_visible || 'Plano General';
                const weekLabel = currentActivePlan ? `Semana #${currentActivePlan.id} (${currentActivePlan.startDate} al ${currentActivePlan.endDate})` : 'Planificación';
                const pageCounterStr = plansToExportIds.length > 1 ? `  |  Plano ${i + 1} de ${plansToExportIds.length}` : '';
                doc.text(`${weekLabel}  |  Plano: ${planLabel}${pageCounterStr}`, 12, 17);

                const fechaStr = new Date().toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
                doc.text(`Emitido: ${fechaStr}`, pageWidth - 12, 14, { align: 'right' });

                await yieldThread();

                // Layout y cálculo de escalado de imagen
                const margin = 6;
                const topMargin = 24;
                const legendWidth = 72;

                const availableWidth = pageWidth - (margin * 2) - legendWidth - 4;
                const availableHeight = pageHeight - topMargin - margin;

                const imgProps = doc.getImageProperties(dataUrl);
                const actualImgW = imgProps.width || dims.width || 1;
                const actualImgH = imgProps.height || dims.height || 1;
                const imgAspect = actualImgW / actualImgH;

                let imgW = availableWidth;
                let imgH = imgW / imgAspect;

                if (imgH > availableHeight) {
                    imgH = availableHeight;
                    imgW = imgH * imgAspect;
                }

                const imgX = margin + (availableWidth - imgW) / 2;
                const imgY = topMargin + (availableHeight - imgH) / 2;

                doc.setDrawColor(203, 213, 225);
                doc.rect(imgX - 0.5, imgY - 0.5, imgW + 1, imgH + 1, 'S');
                doc.addImage(dataUrl, 'JPEG', imgX, imgY, imgW, imgH);

                await yieldThread();

                // Leyenda filtrada específicamente para este plano
                const planTasks = getProgrammedTasksForPlan(targetPlanId);
                const legendData = planTasks.map(pt => [
                    '',
                    pt.task.name,
                    pt.isSurface ? 'Área' : 'Puntual',
                    `${pt.unitIds.size}`
                ]);

                if (legendData.length > 0) {
                    const legendX = pageWidth - legendWidth - margin;
                    const estimatedRowsH = Math.min(availableHeight - 12, legendData.length * 6.5 + 20);

                    doc.setFillColor(248, 250, 252);
                    doc.setDrawColor(226, 232, 240);
                    doc.setLineWidth(0.5);
                    doc.roundedRect(legendX, topMargin, legendWidth, estimatedRowsH, 2, 2, 'FD');

                    doc.setFillColor(30, 41, 59);
                    doc.roundedRect(legendX, topMargin, legendWidth, 8, 2, 2, 'F');
                    doc.setTextColor(255, 255, 255);
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.text('LEYENDA DE PLANIFICACIÓN', legendX + legendWidth / 2, topMargin + 5.5, { align: 'center' });

                    await yieldThread();

                    const currentDoc = doc;
                    autoTable(currentDoc, {
                        startY: topMargin + 9,
                        margin: { left: legendX + 1.5, right: margin + 1.5 },
                        tableWidth: legendWidth - 3,
                        head: [['', 'Partida Programada', 'Tipo', 'Cant.']],
                        body: legendData,
                        theme: 'striped',
                        headStyles: {
                            fillColor: [51, 65, 85],
                            textColor: [255, 255, 255],
                            fontSize: 7.5,
                            fontStyle: 'bold',
                            halign: 'center',
                            cellPadding: 1.8
                        },
                        bodyStyles: {
                            fontSize: 7,
                            textColor: [30, 41, 59],
                            valign: 'middle',
                            cellPadding: 1.8,
                            overflow: 'ellipsize'
                        },
                        alternateRowStyles: {
                            fillColor: [241, 245, 249]
                        },
                        columnStyles: {
                            0: { cellWidth: 6, halign: 'center' },
                            1: { cellWidth: 41, fontStyle: 'bold' },
                            2: { cellWidth: 12, halign: 'center', fontSize: 6.5 },
                            3: { cellWidth: 10, halign: 'center', fontStyle: 'bold' }
                        },
                        styles: {
                            lineColor: [226, 232, 240],
                            lineWidth: 0.2
                        },
                        didDrawCell: (data) => {
                            if (data.section === 'body' && data.column.index === 0) {
                                const taskItem = planTasks[data.row.index];
                                if (taskItem) {
                                    const hex = taskItem.color || '#3B82F6';
                                    const r = parseInt(hex.slice(1, 3), 16) || 59;
                                    const g = parseInt(hex.slice(3, 5), 16) || 130;
                                    const b = parseInt(hex.slice(5, 7), 16) || 246;

                                    const cx = data.cell.x + data.cell.width / 2;
                                    const cy = data.cell.y + data.cell.height / 2;

                                    currentDoc.setFillColor(r, g, b);
                                    currentDoc.setDrawColor(255, 255, 255);
                                    currentDoc.setLineWidth(0.3);

                                    if (taskItem.isSurface) {
                                        currentDoc.rect(cx - 2.2, cy - 2.2, 4.4, 4.4, 'FD');
                                    } else if (taskItem.shape === 'circle') {
                                        currentDoc.circle(cx, cy, 2.1, 'FD');
                                    } else if (taskItem.shape === 'diamond') {
                                        currentDoc.lines([[2.1, 2.1], [-2.1, 2.1], [-2.1, -2.1], [2.1, -2.1]], cx, cy - 2.1, [1, 1], 'FD', true);
                                    } else if (taskItem.shape === 'triangle') {
                                        currentDoc.lines([[2.1, 3.8], [-4.2, 0], [2.1, -3.8]], cx, cy - 1.9, [1, 1], 'FD', true);
                                    } else if (taskItem.shape === 'star') {
                                        const outerR = 2.2;
                                        const innerR = 0.95;
                                        const pts: [number, number][] = [];
                                        for (let k = 0; k < 10; k++) {
                                            const rad = (k % 2 === 0 ? outerR : innerR);
                                            const angle = -Math.PI / 2 + (k * Math.PI) / 5;
                                            pts.push([cx + rad * Math.cos(angle), cy + rad * Math.sin(angle)]);
                                        }
                                        const relLines: [number, number][] = [];
                                        for (let k = 1; k < pts.length; k++) {
                                            relLines.push([pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]]);
                                        }
                                        currentDoc.lines(relLines, pts[0][0], pts[0][1], [1, 1], 'FD', true);
                                    } else {
                                        // square
                                        currentDoc.roundedRect(cx - 2.0, cy - 2.0, 4.0, 4.0, 0.4, 0.4, 'FD');
                                    }
                                }
                            }
                        }
                    });
                }

                // Pie de página
                doc.setFontSize(8);
                doc.setTextColor(148, 163, 184);
                doc.text(`K-LyDApp / ConstructOS — Sistema de Gestión y Control de Producción`, margin, pageHeight - 4);
            }

            if (doc) {
                doc.save(`Avance_Grafico_Planificacion_${projectInfo.name.replace(/\s+/g, '_')}_Semana_${currentActivePlan?.id || 1}.pdf`);
            }
        } catch (err) {
            console.error("Error exportando PDF de avance gráfico:", err);
            alert("Ocurrió un error al generar el archivo PDF.");
        } finally {
            if (originalPlanId && selectedPlanId !== originalPlanId) {
                setSelectedPlanId(originalPlanId);
            }
            setIsExportingPdf(false);
        }
    };

    const togglePlanForExport = (planId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedPlanIdsForExport(prev => {
            if (prev.includes(planId)) {
                if (prev.length === 1) return prev; // Mantener al menos 1 seleccionado
                return prev.filter(id => id !== planId);
            }
            return [...prev, planId];
        });
    };

    const toggleSelectAllPlans = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (selectedPlanIdsForExport.length === plans.length) {
            setSelectedPlanIdsForExport(selectedPlanId ? [selectedPlanId] : (plans[0] ? [plans[0].id] : []));
        } else {
            setSelectedPlanIdsForExport(plans.map(p => p.id));
        }
    };

    if (loading) {
        return (
            <div className="p-8 flex items-center justify-center h-full">
                <span className="material-symbols-outlined animate-spin text-4xl text-blue-500">sync</span>
            </div>
        );
    }

    if (plans.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-[550px] bg-slate-50 dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-8 text-center">
                <span className="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-600 mb-4">map</span>
                <h2 className="text-xl font-bold text-slate-700 dark:text-slate-200 mb-2">Sin Planos Configurados</h2>
                <p className="text-slate-500 dark:text-slate-400 max-w-md">
                    Este proyecto aún no tiene planos cargados. Dirígete a E.D.T y selecciona "Configuración de Avance Gráfico" para subir planos y mapear los recintos.
                </p>
                {onClose && (
                    <button onClick={onClose} className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
                        Cerrar Visor
                    </button>
                )}
            </div>
        );
    }

    return (
        <div 
            ref={containerRef} 
            className="flex flex-col h-full w-full bg-slate-100 dark:bg-slate-950 overflow-hidden relative select-none rounded-xl"
        >
            {/* Barra Superior Fija con Selector Multi-Plano y Botones de Acción */}
            <div className="shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 py-2 flex items-center justify-between gap-3 z-20">
                <div className="flex items-center gap-2.5 flex-wrap">
                    {/* Selector de Plano (Visualización y Selección Múltiple para PDF) */}
                    <div className="relative flex items-center gap-1.5" ref={planDropdownRef}>
                        <span className="material-symbols-outlined text-blue-600 text-[18px]">map</span>
                        <span className="font-extrabold text-slate-800 dark:text-white text-xs uppercase tracking-wide">Plano:</span>
                        <button
                            type="button"
                            onClick={() => setIsPlanDropdownOpen(prev => !prev)}
                            title={activePlan?.nombre_visible || 'Seleccionar plano'}
                            className="bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg px-2.5 py-1 text-xs text-slate-800 dark:text-white font-medium outline-none hover:border-blue-400 focus:ring-1 focus:ring-blue-500 flex items-center gap-2 max-w-[260px]"
                        >
                            <span className="truncate">
                                {activePlan ? activePlan.nombre_visible : 'Seleccionar plano'}
                            </span>
                            {selectedPlanIdsForExport.length > 1 && (
                                <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-[10px] font-extrabold px-1.5 py-0.2 rounded-full shrink-0">
                                    {selectedPlanIdsForExport.length} sel.
                                </span>
                            )}
                            <span className="material-symbols-outlined text-[16px] text-slate-400 shrink-0">
                                {isPlanDropdownOpen ? 'expand_less' : 'expand_more'}
                            </span>
                        </button>

                        {isPlanDropdownOpen && (
                            <div className="absolute left-0 top-full mt-1.5 w-80 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl z-50 overflow-hidden">
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                        Marca para exportar en PDF
                                    </span>
                                    <button
                                        type="button"
                                        onClick={toggleSelectAllPlans}
                                        className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        {selectedPlanIdsForExport.length === plans.length ? 'Solo actual' : 'Seleccionar todos'}
                                    </button>
                                </div>
                                <div className="max-h-60 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                                    {plans.map(p => {
                                        const revMatch = p.nombre_original?.match(/\|rev:(\d+)$/);
                                        const rev = revMatch ? ` (Rev. ${revMatch[1]})` : ' (Rev. 1)';
                                        const isViewing = p.id === selectedPlanId;
                                        const isChecked = selectedPlanIdsForExport.includes(p.id);
                                        return (
                                            <div
                                                key={p.id}
                                                className={`flex items-center gap-2.5 px-3 py-2 text-xs transition-colors cursor-pointer ${
                                                    isViewing
                                                        ? 'bg-blue-50/80 dark:bg-blue-950/40 font-bold text-blue-700 dark:text-blue-300'
                                                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-200'
                                                }`}
                                                onClick={() => {
                                                    setSelectedPlanId(p.id);
                                                    if (!selectedPlanIdsForExport.includes(p.id)) {
                                                        setSelectedPlanIdsForExport(prev => [...prev, p.id]);
                                                    }
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={() => {}}
                                                    onClick={(e) => togglePlanForExport(p.id, e)}
                                                    className="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                                                    title="Incluir este plano en la descarga PDF"
                                                />
                                                <div className="flex-1 min-w-0 truncate" title={`${p.nombre_visible}${rev}`}>
                                                    {p.nombre_visible}{rev}
                                                </div>
                                                {isViewing && (
                                                    <span className="text-[9px] uppercase font-extrabold bg-blue-600 text-white px-1.5 py-0.5 rounded shrink-0">
                                                        Viendo
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="h-4 w-[1px] bg-slate-200 dark:bg-slate-700 hidden sm:block"></div>

                    {/* Botón Descargar PDF */}
                    <button
                        onClick={handleExportPdf}
                        disabled={isExportingPdf || !activePlan}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition-all shadow-sm disabled:opacity-50"
                        title="Descargar plano(s) seleccionado(s) en un mismo PDF con membrete y leyenda"
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {isExportingPdf ? 'sync' : 'picture_as_pdf'}
                        </span>
                        <span>
                            {isExportingPdf
                                ? 'Generando...'
                                : selectedPlanIdsForExport.length > 1
                                    ? `Descargar PDF (${selectedPlanIdsForExport.length} planos)`
                                    : 'Descargar PDF'}
                        </span>
                    </button>

                    {/* Botón Pantalla Completa / Ampliar Vista */}
                    <button
                        onClick={handleToggleFullscreen}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all border border-slate-200 dark:border-slate-700 shadow-sm"
                        title={isFullscreen ? "Restaurar tamaño normal" : "Ampliar visor a pantalla completa"}
                    >
                        <span className="material-symbols-outlined text-[16px]">
                            {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                        </span>
                        <span>{isFullscreen ? 'Salir' : 'Agrandar Pantalla'}</span>
                    </button>
                </div>
            </div>

            {/* Canvas y Leyenda Lateral */}
            <div className="flex-1 min-h-0 w-full flex overflow-hidden relative">
                {/* Visor de Plano Konva */}
                <div className="flex-1 w-full h-full">
                    {activePlan ? (
                        <PdfKonvaViewer
                            ref={konvaViewerRef}
                            pdfUrl={`${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/graphic_plans/${activePlan.ruta_storage}`}
                            regions={konvaRegions}
                            interactive={false}
                            selectedRegionId={popoverInfo?.unit?.id}
                            onRegionClick={(reg: any) => handleRegionSelect(reg)}
                        />
                    ) : (
                        <div className="flex items-center justify-center h-full text-slate-400 font-medium">
                            Selecciona un plano para visualizar
                        </div>
                    )}
                </div>

                {/* Leyenda Dinámica Lateral (Partidas Programadas del Plano Actual) */}
                <div className="w-80 border-l border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md shadow-2xl flex flex-col z-30 shrink-0">
                    <div className="p-3.5 border-b border-slate-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-800/80 flex items-center justify-between">
                        <div className="min-w-0 pr-2">
                            <div className="text-[10px] uppercase font-black tracking-wider text-blue-600 dark:text-blue-400">
                                Leyenda de Planificación
                            </div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white truncate">
                                Partidas de la Semana
                            </h4>
                        </div>
                        <span className="text-xs font-bold bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300 px-2 py-0.5 rounded-full shrink-0">
                            {weeklyProgrammedTasks.length} {weeklyProgrammedTasks.length === 1 ? 'partida' : 'partidas'}
                        </span>
                    </div>

                    <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
                        {weeklyProgrammedTasks.length === 0 ? (
                            <div className="text-center py-10 text-slate-400 text-xs italic">
                                No hay partidas programadas para esta semana en este plano.
                            </div>
                        ) : (
                            weeklyProgrammedTasks.map(({ task, color, isSurface, shape, unitIds }) => (
                                <div
                                    key={task.id}
                                    className="p-2.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 shadow-sm flex flex-col gap-1.5 hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                        {/* Muestra gráfica de la leyenda (Superficie vs Puntual) */}
                                        {isSurface ? (
                                            <div
                                                className="w-4 h-4 rounded border shadow-sm shrink-0"
                                                style={{ backgroundColor: color, borderColor: color }}
                                                title="Partida de Superficie (Relleno / Franja)"
                                            />
                                        ) : (
                                            <div
                                                className="w-5 h-5 flex items-center justify-center shadow-sm shrink-0 border border-slate-300 dark:border-slate-600 rounded bg-slate-100 dark:bg-slate-800"
                                                title={`Partida Puntual (${SHAPE_LABELS[shape]})`}
                                            >
                                                {renderShapeBadgeSvg(shape, color)}
                                            </div>
                                        )}

                                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200 leading-tight truncate flex-1" title={task.name}>
                                            {task.name}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between text-[10px] text-slate-500 font-semibold px-0.5">
                                        <span className="flex items-center gap-1">
                                            <span className="material-symbols-outlined text-[12px]">
                                                {isSurface ? 'square_foot' : 'pin_drop'}
                                            </span>
                                            {isSurface ? 'Superficie (m²)' : `${SHAPE_LABELS[shape]} (${task.unit || 'un'})`}
                                        </span>
                                        <span className="bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded text-slate-600 dark:text-slate-300 font-bold">
                                            {unitIds.size} {unitIds.size === 1 ? 'recinto' : 'recintos'}
                                        </span>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Resumen de Tipos de Renderizado */}
                    <div className="p-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 text-[11px] text-slate-500 flex flex-col gap-1.5">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded bg-blue-500/40 border border-blue-500"></div>
                            <span><strong>Franjas / Área:</strong> Partidas en m²</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-amber-500 border border-white"></div>
                            <span><strong>Símbolos:</strong> Partidas en un, pza, m</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Popover Informativo Flotante por Unidad Física */}
            {popoverInfo && (
                <div
                    className="fixed z-50 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md rounded-xl shadow-2xl border border-slate-200 dark:border-slate-800 p-3.5 w-80 animate-in fade-in zoom-in-95 duration-150"
                    style={{ left: `${popoverInfo.x}px`, top: `${popoverInfo.y}px` }}
                >
                    <div className="flex items-start justify-between gap-2 border-b border-slate-200 dark:border-slate-700 pb-2 mb-2.5">
                        <div>
                            <div className="text-[10px] uppercase font-black tracking-wider text-blue-600 dark:text-blue-400">
                                {popoverInfo.level ? popoverInfo.level.name : 'Recinto'}
                            </div>
                            <h4 className="font-extrabold text-sm text-slate-900 dark:text-white">
                                {popoverInfo.unit.name}
                            </h4>
                        </div>
                        <button
                            onClick={() => setPopoverInfo(null)}
                            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            <span className="material-symbols-outlined text-[16px]">close</span>
                        </button>
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {popoverInfo.tasks.length === 0 ? (
                            <p className="text-xs text-slate-400 italic text-center py-2">
                                Sin partidas programadas en esta semana para este recinto.
                            </p>
                        ) : (
                            popoverInfo.tasks.map((item, idx) => (
                                <div
                                    key={idx}
                                    className="p-2 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/80 flex flex-col gap-1 text-xs"
                                >
                                    <div className="flex items-start justify-between gap-1.5">
                                        <span className="font-bold text-slate-800 dark:text-slate-200 leading-snug">
                                            {item.task.name}
                                        </span>
                                        <span className={`text-[9px] font-extrabold px-1.5 py-0.5 rounded border uppercase shrink-0 ${item.statusColor}`}>
                                            {item.statusText}
                                        </span>
                                    </div>
                                    <div className="flex items-center justify-between text-[10px] text-slate-500">
                                        <span>Subcontrato:</span>
                                        <span className="font-semibold text-slate-700 dark:text-slate-300 truncate max-w-[140px]" title={item.subcontractorName}>
                                            {item.subcontractorName}
                                        </span>
                                    </div>
                                    {item.percentComprometido !== undefined && (
                                        <div className="flex items-center justify-between text-[10px] text-slate-500">
                                            <span>% Comprometido:</span>
                                            <span className="font-bold text-blue-600 dark:text-blue-400">{item.percentComprometido}%</span>
                                        </div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
